/** Pieces both platform pages draw with, so the list and the cell page read alike. */

import type { ReactElement, ReactNode } from "react";

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ProvisioningReport } from "@/lib/platform";
import { STEP_LABELS, STEP_STATUS_LABELS } from "@/platform/labels";

export const INPUT =
  "mt-1 w-full border border-input bg-background px-3 py-2 text-sm";

export const SECTION_HEADING = "text-lg font-bold";

/** `undefined` while it is being read, `null` when it could not be. */
export function revisionText(
  current: string | null | undefined,
  head: string | null | undefined,
): string {
  if (current === undefined) {
    return "…";
  }
  if (current === null) {
    return "unbekannt";
  }
  if (current === head) {
    return `${current} (aktuell)`;
  }
  return head ? `${current}, Ziel ${head}` : current;
}

export function options(
  labels: Readonly<Record<string, string>>,
): ReactElement[] {
  return Object.entries(labels).map(([value, label]) => (
    <option key={value} value={value}>
      {label}
    </option>
  ));
}

/** The answer to the last button, announced to screen readers as it changes. */
export function Notice({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  return (
    <p
      className="mt-3 whitespace-pre-wrap border border-border bg-surface-sunken px-4 py-3 text-sm"
      role="status"
      aria-live="polite"
    >
      {children}
    </p>
  );
}

/**
 * The steps of a create or delete run. "Von Hand" is what the run could not do itself
 * (backup and monitoring are never automated), so it stays on screen after the run.
 */
export function ReportTable({
  report,
}: {
  readonly report: ProvisioningReport;
}): ReactElement {
  return (
    <Table className="mt-4">
      <TableCaption className="sr-only">Schritte für {report.slug}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Schritt</TableHead>
          <TableHead>Stand</TableHead>
          <TableHead>Von Hand</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {report.steps.map((step) => (
          <TableRow key={step.name}>
            <TableCell>{STEP_LABELS[step.name] ?? step.name}</TableCell>
            <TableCell>{STEP_STATUS_LABELS[step.status]}</TableCell>
            <TableCell className="whitespace-pre-wrap">
              {step.manual_instruction}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
